// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { TextFieldProps } from "@stoneage/MobiusOverrides/TextField/TextField";
import Close from "@stoneage/XDL/Icons/IconCloseInactive";
import { fontConfig } from "@stoneage/XDL/constants";
import { color, getSystemColor, xScreen } from "@stoneage/XDL/Utilities";
import translate from "@insite/client-framework/Translate";
import { ButtonIconPresentationProps, ButtonPresentationProps } from "@insite/mobius/Button";
import { DatePickerPresentationProps } from "@insite/mobius/DatePicker";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconWrapper } from "@insite/mobius/Icon";
import { TagPresentationProps } from "@insite/mobius/Tag";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";
import IconClear from "@stoneage/XDL/Icons/IconClear";

interface MyAccountFilterSectionStyles {
    filterContainer?: GridContainerProps;
    countGridItem?: GridItemProps;
    toggleFilterGridItem?: GridItemProps;
    toggleFilterButton?: ButtonPresentationProps;
    toggleFilterButtonIcon?: ButtonIconPresentationProps;
    clearButton?: ButtonPresentationProps;
    datesGridItem?: GridItemProps;
    datePickersWrapper?: InjectableCss;
    fromDate?: DatePickerPresentationProps;
    toDate?: DatePickerPresentationProps;
    smallGridItem?: GridItemProps;
    mediumGridItem?: GridItemProps;
    orderTotalGridItem?: GridItemProps;
    orderTotalAmountTextField?: TextFieldPresentationProps;
    appliedFilterTag?: TagPresentationProps;
    appliedFiltersContainer?: InjectableCss;
    searchFieldWrapper?: InjectableCss;
}

const myAccountFilterSectionStyles: MyAccountFilterSectionStyles = {
    filterContainer: {
        css: css`
            padding-top: 20px;
        `,
    },
    clearButton: {
        variant: "link" as any,
        hoverStyle: {
            color: getSystemColor("primary"),
        },
        icon: {
            src: IconClear,
            position: "left",
        },
        typographyProps: {
            ...fontConfig.smallSingleLine,
            weight: fontConfig.weight.medium,
        },
        css: css`
            white-space: nowrap;
            ${TypographyStyle} {
                font-weight: ${fontConfig.weight.medium};
            }
            ${IconWrapper} {
                &,
                svg {
                    width: 12px;
                    height: 12px;
                    color: ${color("common.ui1")};

                    &:hover {
                        color: ${color("primary")};
                    }
                }
            }
        `,
    },
    orderTotalGridItem: {
        width: [12, 12, 12, 4, 4],
    },
    mediumGridItem: {
        width: [12, 6, 6, 6, 6],
        css: css`
            > div {
                width: 100%;
            }
        `,
    },
    orderTotalAmountTextField: {
        cssOverrides: {
            formInputWrapper: css`
                margin-top: 0;
            `,
        },
        label: translate("Amount"),
    } as TextFieldProps,
    datesGridItem: {
        width: [12, 12, 12, 4, 4],
    },
    fromDate: {
        mergeCss: true,
        cssOverrides: {
            datePicker: css`
                min-width: 140px;
            `,
            formField: css`
                width: 100%;
            `,
        },
    },
    smallGridItem: {
        width: [12, 6, 3, 3, 2],
    },
    datePickersWrapper: {
        css: css`
            display: flex;
            > div {
                width: 50%;
            }
            ${xScreen("lt", "sm")} {
                flex-direction: column;
                > div {
                    width: auto;
                }
            }
        `,
    },
    toDate: {
        mergeCss: true,
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
            datePicker: css`
                min-width: 140px;
                ${xScreen("lte", "md")} {
                    .react-calendar {
                        left: unset;
                        right: 0;
                    }
                }
            `,
        },
    },
    appliedFiltersContainer: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
        `,
    },
    searchFieldWrapper: {
        css: css`
            display: flex;
            flex: 0 1 calc(100% / 6 - 22px);
            min-width: 130px;
            ${xScreen("lte", "md")} {
                flex: 0 1 calc(100% / 3 - 15px);
            }
            &:not(.mobile-half-column) {
                ${xScreen("lt", "sm")} {
                    min-width: 100%;
                }
            }
            &.mobile-half-column {
                ${xScreen("lt", "sm")} {
                    flex: 0 1 calc(100% / 2 - 12px);
                }
            }
        `,
    },
};

export default myAccountFilterSectionStyles;
